import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Printer, Search, SlidersHorizontal } from "lucide-react";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { downloadCSV } from "@/lib/erpdocs";
import { getReportByKey } from "@/lib/reportCatalog";
import PrintBrandHeader from "@/components/PrintBrandHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const displayValue = (value, format) => {
  if (value === null || value === undefined || value === "") return "—";
  const kind = String(format || "").toLowerCase();
  if (["currency", "money", "inr", "amount"].includes(kind)) {
    const number = Number(value);
    return Number.isFinite(number) ? inr(number) : "—";
  }
  if (["number", "numeric", "decimal", "quantity"].includes(kind)) {
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : String(value);
  }
  if (["percent", "percentage"].includes(kind)) {
    const number = Number(value);
    return Number.isFinite(number) ? `${number.toLocaleString("en-IN", { maximumFractionDigits: 2 })}%` : String(value);
  }
  return String(value);
};

const metricValue = (metric) => displayValue(metric.value, metric.format);
const safeCsvCell = (value) => {
  if (typeof value !== "string") return value;
  return /^[\s\uFEFF]*[=+\-@]/u.test(value) ? `'${value}` : value;
};

export default function ExpandedReport() {
  const { key } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromQuery = searchParams.get("from_date") || "";
  const toQuery = searchParams.get("to_date") || "";
  const report = getReportByKey(key);
  const [fromDate, setFromDate] = useState(fromQuery);
  const [toDate, setToDate] = useState(toQuery);
  const [data, setData] = useState(null);
  const [company, setCompany] = useState(null);
  const [companyError, setCompanyError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [dateError, setDateError] = useState("");
  const [rowSearch, setRowSearch] = useState("");
  const [filterColumn, setFilterColumn] = useState("");

  useEffect(() => {
    setRowSearch("");
    setFilterColumn("");
  }, [key]);

  useEffect(() => {
    setFromDate(fromQuery);
    setToDate(toQuery);
  }, [fromQuery, toQuery]);

  useEffect(() => {
    let active = true;
    http.get("/settings")
      .then((response) => { if (active) setCompany(response.data); })
      .catch((requestError) => {
        if (active) setCompanyError(requestErrorMessage(requestError, "load company details"));
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    if (!report) {
      setLoading(false);
      setError("This report could not be found.");
      return () => { active = false; };
    }
    setLoading(true);
    const params = {};
    if (fromQuery) params.from_date = fromQuery;
    if (toQuery) params.to_date = toQuery;
    http.get(`/reports/expanded/${encodeURIComponent(key)}`, { params })
      .then((response) => {
        if (active) {
          if (response.data) setData(response.data);
          else setError("The report returned no data. Please retry.");
        }
      })
      .catch((requestError) => {
        if (!active) return;
        setError(requestErrorMessage(requestError, "load this report"));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [key, report, fromQuery, toQuery, retry]);

  const columns = Array.isArray(data?.columns) ? data.columns : [];
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const searchTerm = rowSearch.trim().toLocaleLowerCase();
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const searchable = filterColumn ? columns.filter((column) => column.key === filterColumn) : columns;
    return rows.filter((row) => searchable.some((column) =>
      String(row?.[column.key] ?? "").toLocaleLowerCase().includes(searchTerm)));
  }, [rows, columns, filterColumn, searchTerm]);
  const csvRows = filteredRows.map((row) => columns.map((column) => safeCsvCell(row?.[column.key] ?? "")));

  const applyDates = (event) => {
    event.preventDefault();
    if (fromDate && toDate && fromDate > toDate) {
      setDateError("From date must be on or before To date.");
      return;
    }
    setDateError("");
    const next = new URLSearchParams(searchParams);
    if (fromDate) next.set("from_date", fromDate);
    else next.delete("from_date");
    if (toDate) next.set("to_date", toDate);
    else next.delete("to_date");
    setSearchParams(next);
  };

  const resetDates = () => {
    setDateError("");
    setFromDate("");
    setToDate("");
    const next = new URLSearchParams(searchParams);
    next.delete("from_date");
    next.delete("to_date");
    setSearchParams(next);
  };

  const exportCsv = () => {
    if (!data || !columns.length) return;
    const safeName = (data.title || report?.label || key).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    downloadCSV(`${safeName || "report"}.csv`, columns.map((column) => safeCsvCell(column.label)), csvRows);
  };

  const printFrom = data?.from_date || fromQuery;
  const printTo = data?.to_date || toQuery;
  const printPeriod = printFrom && printTo
    ? `Period: ${printFrom} – ${printTo}`
    : printFrom
      ? `From: ${printFrom}`
      : printTo
        ? `Through: ${printTo}`
        : "Period: All dates";

  return (
    <div id="print-area" className="p-4 sm:p-6 lg:p-8 space-y-6 print:bg-white print:text-slate-900">
      <PrintBrandHeader
        company={company}
        title={data?.title || report?.label || "Report"}
        subtitle={printPeriod}
      />
      {companyError && <p className="text-sm text-amber-700" role="status">{companyError}. Report data is still available.</p>}

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/reports" className="inline-flex items-center gap-1 text-sm text-brand-700 hover:underline mb-2 print:hidden">
            <ArrowLeft className="h-4 w-4" /> Reports Center / {report?.group || "Report"}
          </Link>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{data?.title || report?.label || "Report"}</h1>
          {(data?.description || report) && <p className="text-sm text-muted-foreground mt-1">{data?.description || report?.group}</p>}
        </div>
        <div className="flex gap-2 print:hidden">
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!data || !columns.length || filteredRows.length === 0} data-testid="expanded-report-csv">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button size="sm" variant="print" onClick={() => window.print()} disabled={!data || !!error} data-testid="expanded-report-print">
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
        </div>
      </div>

      <Card className="p-4 space-y-4 print:hidden">
        <h2 className="flex items-center gap-2 font-heading font-semibold text-sm"><SlidersHorizontal className="h-4 w-4" /> Filters</h2>
        <form onSubmit={applyDates} className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><label className="text-xs font-medium text-muted-foreground" htmlFor="report-from-date">From date</label><Input id="report-from-date" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} className="w-40" /></div>
          <div className="space-y-1"><label className="text-xs font-medium text-muted-foreground" htmlFor="report-to-date">To date</label><Input id="report-to-date" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} className="w-40" /></div>
          <Button type="submit" size="sm" data-testid="expanded-report-apply">Apply dates</Button>
          <Button type="button" size="sm" variant="outline" onClick={resetDates}>All dates</Button>
        </form>
        {dateError && <p role="alert" className="text-sm text-rose-700">{dateError}</p>}
        {data && columns.length > 0 && (
          <div className="flex flex-wrap items-end gap-3 border-t pt-3">
            <div className="space-y-1 flex-1 min-w-48 max-w-sm">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="report-row-search">Search rows</label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="report-row-search" type="search" placeholder="Search this report" value={rowSearch} onChange={(event) => setRowSearch(event.target.value)} className="pl-9" />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="report-filter-column">Search in</label>
              <select id="report-filter-column" value={filterColumn} onChange={(event) => setFilterColumn(event.target.value)}
                className="flex h-10 w-full min-w-40 rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">All columns</option>
                {columns.map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}
              </select>
            </div>
            {rowSearch && <Button size="sm" variant="outline" type="button" onClick={() => setRowSearch("")}>Clear search</Button>}
          </div>
        )}
      </Card>

      {loading && <div className="py-10 text-center text-muted-foreground" role="status">Loading report…</div>}
      {!loading && error && <Card className="p-5 text-sm text-rose-700" role="alert">
        <p>{error}</p>
        {report && <Button size="sm" variant="outline" className="mt-3" onClick={() => setRetry((value) => value + 1)}>Retry</Button>}
      </Card>}
      {!loading && !error && data && (
        <div className="space-y-4">
          {Array.isArray(data.metrics) && data.metrics.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {data.metrics.map((metric, index) => (
                <Card key={`${metric.label}-${index}`} className="p-4">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{metric.label}</div>
                  <div className="font-heading text-xl font-bold mt-1 tabular">{metricValue(metric)}</div>
                </Card>
              ))}
            </div>
          )}
          {data.note && <Card className="p-3 text-sm text-muted-foreground">{data.note}</Card>}
          <p className="text-xs text-muted-foreground">
            {searchTerm ? `Showing ${filteredRows.length} of ${rows.length} rows. Summary figures reflect the date range, not the row search.` : `${rows.length} rows · ${printPeriod}`}
            {searchTerm && ` · Search: ${rowSearch.trim()} (${filterColumn ? columns.find((column) => column.key === filterColumn)?.label : "all columns"})`}
          </p>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto" role="region" aria-label={`${data.title || report?.label || "Report"} table`} tabIndex={0}>
              <table className="w-full text-sm" aria-label={`${data.title || report?.label || "Report"} data`}>
                <caption className="sr-only">{data.title || report?.label || "Report"} data for {printPeriod}</caption>
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    {columns.map((column) => <th key={column.key} scope="col" className="px-4 py-3 font-medium whitespace-nowrap">{column.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, rowIndex) => (
                    <tr key={row.id ?? rowIndex} className="border-t hover:bg-slate-50">
                      {columns.map((column) => (
                        <td key={column.key} className={`px-4 py-3 whitespace-nowrap ${["currency", "money", "inr", "amount", "number", "numeric", "decimal", "quantity", "percent", "percentage"].includes(String(column.format || "").toLowerCase()) ? "text-right font-mono tabular-nums" : ""}`}>
                          {displayValue(row?.[column.key], column.format)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {filteredRows.length === 0 && <tr><td colSpan={Math.max(columns.length, 1)} className="px-4 py-10 text-center text-muted-foreground">{searchTerm ? "No rows match your search." : "No data for the selected date range."}</td></tr>}
                </tbody>
                {data.totals && !searchTerm && rows.length > 0 && <tfoot className="bg-slate-50 border-t font-semibold"><tr>{columns.map((column, index) => <td key={column.key} className={`px-4 py-3 ${index > 0 ? "text-right font-mono tabular-nums" : ""}`}>{data.totals[column.key] === undefined ? (index === 0 ? "Total" : "") : displayValue(data.totals[column.key], column.format)}</td>)}</tr></tfoot>}
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}